import React, { useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import Loading from "../../components/Loading";
import { PayrollBreakdownStatus, RepetitionType, SummaryRow } from "../../__generated__/graphql";
import VStack from "../../ui_atoms/VStack";
import { TitleMediumText } from "../../components/FormattedText";
import LabelValueText from "../../components/LabelValueText";
import { formatCurrency, normalizeAmountsInText } from "../../utils";
import Callout from "../../ui_atoms/Callout";
import ScreenCardHeadband from "../ScreenCardHeadband";
import { IconName } from "../../components/Icon";
import { useTheme } from "../../hooks/use-theme";

export type SummaryRowWithGross = SummaryRow & {
    // Gross amount (cents) the worker receives. The row's unitPrice is the invoiced
    // amount including charges, so the label shows this one next to the name.
    grossAmount?: number;
};

/**
 * Attach the gross amounts to the bonus and auxiliary salary rows, which the server rows
 * don't carry (their unitPrice is the invoiced amount). Rows are matched positionally per
 * category — the server builds one line per entry in array order, the same assumption the
 * editor's edit/delete row matching relies on.
 */
export const attachGrossAmountsToSummaryRows = (
    rows: SummaryRow[],
    source: {
        bonuses?: Array<{ amount?: number | null }> | null;
        auxiliarySalaries?: Array<{ grossAmount?: number | null }> | null;
    },
): SummaryRowWithGross[] => {
    let bonusIndex = 0;
    let auxiliarySalaryIndex = 0;
    return rows.map((row) => {
        if (row.category === "bonus") {
            const bonus = source.bonuses?.[bonusIndex++];
            return bonus?.amount != null ? { ...row, grossAmount: bonus.amount } : row;
        }
        if (row.category === "auxiliary_salary") {
            const auxiliarySalary = source.auxiliarySalaries?.[auxiliarySalaryIndex++];
            return auxiliarySalary?.grossAmount != null
                ? { ...row, grossAmount: auxiliarySalary.grossAmount }
                : row;
        }
        return row;
    });
};

type SubValuesType = SummaryRowWithGross & {
    onPress?: () => void;
    icon?: IconName;
    extra?: object;
};

export type SummaryRowWithSubValues = SummaryRowWithGross & {
    subValues?: SubValuesType[];
};

type SummaryRowWithSubValuesWithoutCategory = Omit<SummaryRowWithSubValues, "category">;

type PayrollBreakdownSummaryProps = {
    rows?: SummaryRowWithGross[];
    payrollBreakdownStatus: PayrollBreakdownStatus;
    needProcessing?: boolean;
    loading: boolean;
    error?: Error;
};

// Product groups order
const PRODUCT_GROUPS_ORDER = [
    "salary",
    "auxiliary_salary",
    "vacation_compensation",
    "dynamic_fee",
    "variable_fee",
    "deduction",
    "bonus",
    "effective_fee",
    "absence",
];

// Pre-calculate a fixed dictionnary { category -> index }
const PRODUCT_GROUPS_ORDER_MAP = new Map(PRODUCT_GROUPS_ORDER.map((cat, i) => [cat, i]));

const productGroupsSorter = (
    a: SummaryRowWithSubValuesWithoutCategory,
    b: SummaryRowWithSubValuesWithoutCategory,
) => {
    return (
        (PRODUCT_GROUPS_ORDER_MAP.get(a.productKey) ?? Infinity) -
        (PRODUCT_GROUPS_ORDER_MAP.get(b.productKey) ?? Infinity)
    );
};

// Product groups order
const SALARY_PRODUCTS_ORDER = [
    "salary_monthly_full",
    "salary_monthly_partial",
    "salary_daily",
    "salary_hourly",
    "salary_package",
    "salary_overtime_0_from_balance",
    "salary_overtime_0",
    "salary_overtime_25",
    "salary_overtime_50",
    "salary_overtime_100",
];

// Pre-calculate a fixed dictionnary { category -> index }
const SALARY_PRODUCTS_ORDER_MAP = new Map(SALARY_PRODUCTS_ORDER.map((cat, i) => [cat, i]));

const salaryProductsSorter = (
    a: SummaryRowWithSubValuesWithoutCategory,
    b: SummaryRowWithSubValuesWithoutCategory,
) => {
    return (
        (SALARY_PRODUCTS_ORDER_MAP.get(a.productKey) ?? Infinity) -
        (SALARY_PRODUCTS_ORDER_MAP.get(b.productKey) ?? Infinity)
    );
};

/*
 ** Those categories doesn't have a CHF value to display (absences)
 */
const categoryHasValue = (category: string) => !["absence"].includes(category);

const PayrollBreakdownSummary = ({
    rows = [],
    payrollBreakdownStatus,
    needProcessing = false,
    loading,
    error,
}: PayrollBreakdownSummaryProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();

    const summaryRows = useMemo(
        () =>
            rows.map((v) => {
                // The detailed description is the only label built server side that embeds an
                // amount, and it comes formatted with a French locale: rewrite it the Swiss way
                // so that a fee detail reads like every other amount of the summary. The plain
                // description is left alone, it carries free text typed by the employer.
                const row = {
                    ...v,
                    detailedDescription:
                        v.detailedDescription && normalizeAmountsInText(v.detailedDescription),
                };

                return row.repetition === RepetitionType.Count
                    ? { ...row, category: "variable_fee" }
                    : row;
            }),
        [rows],
    );

    const getSubLabelValue = useCallback(
        (item: SubValuesType) => {
            const {
                category,
                productKey,
                quantity,
                description,
                detailedDescription,
                unitPrice,
                onPress,
                icon,
                extra,
                repetition,
                grossAmount,
            } = item;

            let title: string;
            // absence are special rows that already contains the description
            if (category === "absence") {
                title =
                    description ||
                    i18n.t("screens.payroll_breakdown_editor.product_categories.absence");
            } else if (category === "variable_fee") {
                // Variable fees come with a backend description already in the right language,
                // so display it as-is rather than rebuilding the label with quantity/unit price.
                title =
                    detailedDescription ||
                    i18n.t("screens.payroll_breakdown_editor.product_categories.variable_fee");
            } else if (category === "auxiliary_salary") {
                // The backend description already carries the title and the formatted duration
                // ("Réceptionniste – 12h30").
                title =
                    description ||
                    i18n.t("screens.payroll_breakdown_editor.product_categories.auxiliary_salary");
            } else {
                title = i18n.t(`screens.payroll_breakdown_editor.product_keys.${productKey}`, {
                    count: quantity,
                    repetition: i18n.t(
                        `screens.payroll_breakdown_editor.repetitions.${repetition}`,
                        {
                            count: quantity,
                        },
                    ),
                    unitPrice: formatCurrency({
                        amountInCents: item.unitPrice,
                    }),
                    description,
                    ...extra,
                });
            }

            if (grossAmount != null) {
                title = i18n.t("screens.payroll_breakdown_editor.with_gross_amount", {
                    title,
                    amount: formatCurrency({
                        amountInCents: grossAmount,
                        displayDigitsWhenNoCents: false,
                    }),
                });
            }

            return {
                label: title,
                value: categoryHasValue(category)
                    ? formatCurrency({
                          amountInCents: unitPrice * quantity,
                          displayCurrencySymbol: false,
                      })
                    : undefined,
                onPress,
                icon,
            };
        },
        [i18n],
    );

    const getLabelValue = useCallback(
        (item: SummaryRowWithSubValuesWithoutCategory) => {
            const { productKey, quantity, description, unitPrice, subValues = [] } = item;

            const title = i18n.t(
                `screens.payroll_breakdown_editor.product_categories.${productKey}`,
                {
                    count: item.quantity,
                    unitPrice: formatCurrency({
                        amountInCents: item.unitPrice,
                    }),
                },
            );

            return {
                label: `${title}${description ? ` : ${description}` : ""}`,
                value: categoryHasValue(productKey)
                    ? formatCurrency({
                          amountInCents:
                              subValues.length === 0
                                  ? unitPrice * quantity
                                  : subValues.reduce((r, v) => r + v.unitPrice * v.quantity, 0),
                          displayCurrencySymbol: false,
                      })
                    : undefined,
                ...(productKey === "salary" && needProcessing
                    ? {
                          icon: "warning" as const,
                          iconColor: colors.onWarningBackground,
                      }
                    : {}),
                subValues: subValues.sort(salaryProductsSorter).map(getSubLabelValue),
            };
        },
        [colors.onWarningBackground, getSubLabelValue, i18n, needProcessing],
    );
    const groupItems = useCallback((items: SummaryRowWithSubValues[], groupCategory: string) => {
        const subValues = items.filter(
            (i) =>
                i.category === groupCategory &&
                (i.unitPrice * i.quantity !== 0 || i.category === "absence"),
        );

        return {
            productKey: groupCategory,
            // Synthetic aggregated group row: no per-row description/repetition applies here.
            description: null,
            detailedDescription: null,
            repetition: null,
            unitPrice: subValues.reduce((r, v) => r + v.unitPrice * v.quantity, 0),
            quantity: 1,
            subValues,
        };
    }, []);

    const groupedInvoicesItems = useMemo<SummaryRowWithSubValuesWithoutCategory[]>(
        () =>
            summaryRows.reduce<Omit<SummaryRowWithSubValues, "category">[]>((result, value) => {
                const rowIndex = result.findIndex((row) => row.productKey === value.category);

                if (rowIndex === -1) {
                    if (
                        // Categories that we want to group
                        [
                            "salary",
                            "auxiliary_salary",
                            "deduction",
                            "effective_fee",
                            "dynamic_fee",
                            "variable_fee",
                            "bonus",
                            "vacation_compensation",
                            "absence",
                        ].includes(value.category)
                    ) {
                        const children = groupItems(summaryRows, value.category);
                        return [...result, children];
                    } else {
                        return [...result, value];
                    }
                } else {
                    return result;
                }
            }, [] as SummaryRowWithSubValuesWithoutCategory[]),
        [groupItems, summaryRows],
    );

    return (
        <VStack h="2/3" gap={1 / 2}>
            <TitleMediumText>{i18n.t("screens.payroll_breakdown_editor.preview")}</TitleMediumText>
            {needProcessing && (
                <Callout
                    icon={"warning"}
                    markdown={i18n.t("screens.payroll_breakdown_editor.preview_need_compute")}
                    mode="warning"
                />
            )}
            {rows.length === 0 ? (
                <Callout
                    markdown={i18n.t("screens.payroll_breakdown_editor.no_data")}
                    mode="info"
                />
            ) : !loading && !error ? (
                <VStack gap={1 / 2}>
                    {groupedInvoicesItems.sort(productGroupsSorter).map((row, index) => {
                        return <LabelValueText key={index} {...getLabelValue(row)} />;
                    })}
                    <LabelValueText
                        label={i18n.t("screens.payroll_breakdown_editor.invoice")}
                        value={formatCurrency({
                            amountInCents: groupedInvoicesItems.reduce(
                                (result, row) =>
                                    result +
                                    (row.subValues
                                        ? row.subValues.reduce(
                                              (r, v) => r + v.unitPrice * v.quantity,
                                              0,
                                          )
                                        : row.unitPrice * row.quantity),
                                0,
                            ),
                            displayCurrencySymbol: false,
                        })}
                        textStyle={{ fontWeight: "bold" }}
                        {...(needProcessing
                            ? {
                                  icon: "warning",
                                  iconColor: colors.onWarningBackground,
                              }
                            : {})}
                    />
                </VStack>
            ) : error ? (
                <ScreenCardHeadband
                    text={i18n.t(
                        error.message || "screens.payroll_breakdown_editor.error_getting_summary",
                    )}
                    mode="error"
                />
            ) : (
                <Loading />
            )}
            {payrollBreakdownStatus === PayrollBreakdownStatus.Open && (
                <Callout
                    markdown={i18n.t("screens.payroll_breakdown_editor.agree_breakdown_is_correct")}
                    mode="secondary"
                />
            )}
        </VStack>
    );
};

export default PayrollBreakdownSummary;
